import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { getQuestionVersion, getQuestionVersions } from "../../../services/questionVersionsService";

/**
 * A question's versions: the list, newest first ({versions, loading, error, retry}), and each version's content,
 * read once on request (a saved version never changes): `requestVersion(number)` reads it (again after a
 * failure), `versionState(number)` is {loading} | {data} | {error}. Mount it per question (key it by the uuid).
 */
export default function useQuestionVersions(uuid) {
  const [list, setList] = useState({ loaded: false, items: [], error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    getQuestionVersions(uuid, { signal: controller.signal })
      .then((items) => setList({ loaded: true, items: Array.isArray(items) ? items : [], error: null }))
      .catch((error) => {
        if (!controller.signal.aborted) setList({ loaded: true, items: [], error });
      });
    return () => controller.abort();
  }, [uuid, attempt]);

  const retry = useCallback(() => {
    setList({ loaded: false, items: [], error: null });
    setAttempt((n) => n + 1);
  }, []);

  const contents = useRef(new Map());
  const [, rerender] = useReducer((n) => n + 1, 0);
  const requestVersion = useCallback(
    (number) => {
      const entry = contents.current.get(number);
      if (entry && !entry.error) return;
      contents.current.set(number, { loading: true });
      rerender();
      getQuestionVersion(uuid, number).then(
        (data) => {
          contents.current.set(number, { data });
          rerender();
        },
        (error) => {
          contents.current.set(number, { error });
          rerender();
        }
      );
    },
    [uuid]
  );
  const versionState = (number) => contents.current.get(number) || { loading: true };

  return { versions: list.items, loading: !list.loaded, error: list.error, retry, requestVersion, versionState };
}
